import React, { useEffect } from 'react';
import type { FC, ReactNode } from 'react';
import { isType } from 'variant';
import { parse, relative } from 'node:path';
import { getBlocksTypesPath } from '@wix/cli-app-definitions';
import { ErrorViewer, useErrorReporter } from '@wix/cli-error-reporting';
import { outputFile } from '@wix/cli-fs';
import type { Logger } from '../vite/logger.js';
import { useProjectModel } from '../model.js';
import { DevCenterComponent } from '../dev-center/components/dev-center-component.js';
import type { BlocksStructure } from './blocks-services-client.js';
import { useBlocksServicesClient } from './useBlocksServicesClient.js';

const getBlocksTypesContent = (
  structure: BlocksStructure
) => `type ComponentsStructureMap = {
   ${Object.entries(structure.applicationStructure.widgets)
     .map(([widgetId, { elements, widgets }]) => {
       const innerElements = [];
       for (const [id, type] of Object.entries(elements)) {
         innerElements.push(`"#${id}": ${type};`);
       }
       for (const [id, { widgetId }] of Object.entries(widgets)) {
         innerElements.push(`"#${id}": WidgetsAPIMap['${widgetId}']['flat'];`);
       }
       return `"${widgetId}": {
          ${innerElements.join('\n')}
        };`;
     })
     .join('\n')}
    ${Object.entries(structure.applicationStructure.panels)
      .map(([widgetId, { elements }]) => {
        const innerElements = [];
        for (const [id, type] of Object.entries(elements)) {
          innerElements.push(`"#${id}": ${type};`);
        }

        return `"${widgetId}": {
          ${innerElements.join('\n')}
        };`;
      })
      .join('\n')}
  };`;

const getWidgetAPIContent = (
  blocksTypesFile: string,
  widgets: Array<DevCenterComponent<'BlocksWidget'>>
) => {
  const { dir } = parse(blocksTypesFile);

  return `type WidgetsAPIMap = {
    ${widgets
      .map(
        (widget) =>
          `"${widget.id}": typeof import('${relative(
            dir,
            widget.apiPath
          )}').default._types;`
      )
      .join('\n')}
    };`;
};

const getTypesFileContent = (
  widgetAPIContent: string,
  blocksTypeContent: string
) => `/// <reference types="@wix/blocks/$w.d.ts" />

  declare module "@wix/blocks/types" {
    ${widgetAPIContent}
    ${blocksTypeContent}
  }
`;

export const BlocksTypesUpdater: FC<{
  logger: Logger;
  children: ReactNode;
}> = ({ logger, children }) => {
  const { model } = useProjectModel();
  const { reportError } = useErrorReporter();
  const blocksServicesClient = useBlocksServicesClient();

  useEffect(() => {
    const {
      components,
      config: { appId, blocks },
      projectFolder,
    } = model;
    const generateContent = async () => {
      if (blocks?.uiRevision) {
        const blocksStructure = await blocksServicesClient.getBlocksStructure(
          appId,
          blocks.uiRevision
        );
        const blocksTypesFile = getBlocksTypesPath(projectFolder);
        const widgetAPIContent = getWidgetAPIContent(
          blocksTypesFile,
          components.filter(isType(DevCenterComponent.BlocksWidget))
        );
        const blocksTypeContent = getBlocksTypesContent(blocksStructure);

        const typesContent = getTypesFileContent(
          widgetAPIContent,
          blocksTypeContent
        );

        await outputFile(blocksTypesFile, typesContent);
      }
    };
    generateContent().catch((error: unknown) => {
      reportError(error);

      logger.info(<ErrorViewer error={error} />);
    });
    // Listening for uiRevision change only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.config.blocks?.uiRevision]);

  return <>{children}</>;
};
